import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  ContactAutocompleteDropdown,
  type ContactAutocompleteDropdownRef,
} from "@/components/ui/contact-autocomplete.tsx";
import type { Contact, Handle } from "@/types/contact.ts";
import { PickerSearchInput } from "./picker-search-input.tsx";
import type { ContactEntry } from "./types.ts";
import { addRecentContact, useRecentContacts } from "./recent-contacts.ts";
import { PICKER_HEADER_CLASS } from "./usePickerKeyboard.ts";

type ContactPickerProps =
  | {
      mode: "single";
      onSelect: (contact: ContactEntry) => void;
      defaultValue?: ContactEntry | null;
      placeholder?: string;
    }
  | {
      mode: "multi";
      selectedContacts: ContactEntry[];
      onAdd: (contact: ContactEntry) => void;
      onRemove: (index: number) => void;
      onDone: () => void;
      placeholder?: string;
    };

/** Convert a Contact + specific Handle to a ContactEntry. */
function toContactEntry(contact: Contact, handle?: Handle): ContactEntry {
  // Prefer `isDefault` over array order — matches
  // `getUserContactPrimaryEmailHandle`'s server-side precedence.
  const emailHandles = contact.handles?.filter((h) => h.type === "emailAddress") ?? [];
  const phoneHandles = contact.handles?.filter((h) => h.type === "phoneNumber") ?? [];
  const primaryEmail = emailHandles.find((h) => h.isDefault) ?? emailHandles[0];
  const primaryPhone = phoneHandles.find((h) => h.isDefault) ?? phoneHandles[0];
  const email = handle?.type === "emailAddress" ? handle.value : primaryEmail?.value;
  const phone = handle?.type === "phoneNumber" ? handle.value : primaryPhone?.value;
  return {
    id: contact.id,
    name: contact.name ?? "",
    email,
    phone,
    isNew: false,
  };
}

/**
 * Contact picker for the scheduling typeahead — supports single-select
 * (appointment flow) and multi-select (meeting flow) modes.
 *
 * Thin wrapper around ContactAutocompleteDropdown in inline mode.
 */
export function ContactPicker(props: ContactPickerProps) {
  if (props.mode === "single") return <SingleContactPicker {...props} />;
  return <MultiContactPicker {...props} />;
}

// ---------------------------------------------------------------------------
// Single mode — pick one contact/free-text and immediately advance
// ---------------------------------------------------------------------------

function SingleContactPicker({
  onSelect,
  defaultValue,
  placeholder,
}: Extract<ContactPickerProps, { mode: "single" }>) {
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<ContactAutocompleteDropdownRef>(null);
  const { recentIds, recentContacts } = useRecentContacts();

  // If navigating back, show the previous selection's name pre-filled
  const selectedIds = useMemo(
    () => (defaultValue?.id ? new Set([defaultValue.id]) : new Set<string>()),
    [defaultValue],
  );

  const handleSelect = useCallback(
    (contact: Contact) => {
      const entry = toContactEntry(contact);
      addRecentContact(contact.id);
      onSelect(entry);
    },
    [onSelect],
  );

  const handleSelectHandle = useCallback(
    (contact: Contact, handle: Handle) => {
      const entry = toContactEntry(contact, handle);
      addRecentContact(contact.id);
      onSelect(entry);
    },
    [onSelect],
  );

  const handleSelectEmail = useCallback(
    (email: string) => {
      onSelect({ name: email, email, isNew: true });
    },
    [onSelect],
  );

  const handleSelectFreeText = useCallback(() => {
    const text = search.trim();
    if (text) onSelect({ name: text, isNew: true });
  }, [search, onSelect]);

  // Free-text row:
  // - "use" variant while the user is typing (index 0 = "+ [text]")
  // - "selected" variant when navigating back with a previous free-text selection
  const selectedFreeText = useMemo(() => {
    const text = search.trim();
    if (text) {
      return { label: text, onSelect: handleSelectFreeText, variant: "use" as const };
    }
    // Show the previous free-text selection when navigating back
    if (defaultValue?.isNew && defaultValue.name) {
      return {
        label: defaultValue.name,
        onSelect: () => onSelect(defaultValue),
        variant: "selected" as const,
      };
    }
    return undefined;
  }, [search, handleSelectFreeText, defaultValue, onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      dropdownRef.current?.handleArrowDown();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      dropdownRef.current?.handleArrowUp();
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      dropdownRef.current?.handleEnter();
    } else if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      const handled = dropdownRef.current?.handleEnter() ?? false;
      if (handled) e.stopPropagation();
    }
  }, []);

  return (
    <div className="flex flex-col">
      <div className={PICKER_HEADER_CLASS} data-sticky-header>
        <PickerSearchInput
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onClear={() => setSearch("")}
          placeholder={placeholder ?? "Search people..."}
          autoFocus
        />
      </div>

      <ContactAutocompleteDropdown
        ref={dropdownRef}
        query={search}
        onSelect={handleSelect}
        onSelectHandle={handleSelectHandle}
        onSelectEmail={handleSelectEmail}
        filterHandleTypes={["emailAddress", "phoneNumber"]}
        inline
        selectedContactIds={selectedIds}
        showAllWhenEmpty
        recentContactIds={recentIds}
        extraContacts={recentContacts}
        selectedFreeText={selectedFreeText}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi mode — pick multiple contacts with toggle behavior
// ---------------------------------------------------------------------------

function MultiContactPicker({
  selectedContacts,
  onAdd,
  onRemove,
  onDone,
  placeholder,
}: Extract<ContactPickerProps, { mode: "multi" }>) {
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<ContactAutocompleteDropdownRef>(null);
  const { recentIds, recentContacts } = useRecentContacts();

  const selectedIds = useMemo(
    () => new Set(selectedContacts.filter((c) => c.id).map((c) => c.id!)),
    [selectedContacts],
  );

  const newContacts = useMemo(
    () =>
      selectedContacts.map((c, i) => ({ entry: c, index: i })).filter(({ entry }) => entry.isNew),
    [selectedContacts],
  );

  // When "add-only", handleSelect never removes — used by Tab and Enter so that
  // pressing these keys on an already-selected contact doesn't deselect it.
  // When "confirm", hitting Enter on an already-selected contact advances the flow.
  const addOnlyRef = useRef<false | "add-only" | "confirm">(false);

  const handleSelect = useCallback(
    (contact: Contact) => {
      if (selectedIds.has(contact.id)) {
        if (addOnlyRef.current === "confirm") {
          onDone();
          return;
        }
        if (addOnlyRef.current === "add-only") return;
        // Mouse click: toggle off
        const idx = selectedContacts.findIndex((c) => c.id === contact.id);
        if (idx >= 0) onRemove(idx);
      } else {
        const entry = toContactEntry(contact);
        onAdd(entry);
        addRecentContact(contact.id);
        setSearch("");
      }
    },
    [selectedIds, selectedContacts, onAdd, onRemove, onDone],
  );

  const handleSelectHandle = useCallback(
    (contact: Contact, handle: Handle) => {
      if (selectedIds.has(contact.id)) {
        if (addOnlyRef.current === "confirm") {
          onDone();
          return;
        }
        if (addOnlyRef.current === "add-only") return;
        const idx = selectedContacts.findIndex((c) => c.id === contact.id);
        if (idx >= 0) onRemove(idx);
      } else {
        const entry = toContactEntry(contact, handle);
        onAdd(entry);
        addRecentContact(contact.id);
        setSearch("");
      }
    },
    [selectedIds, selectedContacts, onAdd, onRemove, onDone],
  );

  const handleSelectEmail = useCallback(
    (email: string) => {
      onAdd({ name: email, email, isNew: true });
      setSearch("");
    },
    [onAdd],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        dropdownRef.current?.handleArrowDown();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        dropdownRef.current?.handleArrowUp();
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        // Add highlighted contact, or confirm and advance if already selected.
        addOnlyRef.current = "confirm";
        dropdownRef.current?.handleEnter();
        addOnlyRef.current = false;
      } else if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        // If the search box is empty and we already have contacts, just advance.
        if (!search.trim() && selectedContacts.length > 0) {
          onDone();
          return;
        }
        // Otherwise add the highlighted contact (if not already selected) then advance.
        addOnlyRef.current = "add-only";
        dropdownRef.current?.handleEnter();
        addOnlyRef.current = false;
        onDone();
      }
    },
    [onDone, search, selectedContacts.length],
  );

  return (
    <div className="flex flex-col">
      {/* Search input */}
      <div className={PICKER_HEADER_CLASS} data-sticky-header>
        <PickerSearchInput
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onClear={() => setSearch("")}
          placeholder={placeholder ?? "Search people..."}
          autoFocus
        />
      </div>

      {/* Newly added contacts (not in address book) — uses the real index for removal */}
      {newContacts.map(({ entry, index }) => (
        <button
          type="button"
          key={`new-${entry.email ?? entry.name}`}
          aria-label={`Remove ${entry.name}`}
          className="flex flex-row items-center gap-2 px-3 py-2 cursor-pointer bg-secondary hover:bg-secondary/80 w-full text-left"
          onMouseDown={(e) => {
            e.preventDefault();
            onRemove(index);
          }}
        >
          <AvatarWithFallback
            size="sm"
            person={{
              name: entry.name,
              picture: undefined,
              value: entry.email ?? "",
            }}
          />
          <div className="flex flex-col min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{entry.name}</div>
            {entry.email && entry.email !== entry.name && (
              <div className="text-xs text-muted-foreground truncate">{entry.email}</div>
            )}
          </div>
          <Badge variant="secondary" size="sm" className="shrink-0">
            New
          </Badge>
          <Check className="size-4 text-primary shrink-0" />
        </button>
      ))}

      {/* Contact list — delegates to the shared dropdown in inline mode */}
      <ContactAutocompleteDropdown
        ref={dropdownRef}
        query={search}
        onSelect={handleSelect}
        onSelectHandle={handleSelectHandle}
        onSelectEmail={handleSelectEmail}
        filterHandleTypes={["emailAddress", "phoneNumber"]}
        inline
        selectedContactIds={selectedIds}
        showAllWhenEmpty
        recentContactIds={recentIds}
        extraContacts={recentContacts}
      />
    </div>
  );
}
